import React, { memo } from 'react';
import classNames from 'classnames';
import Typography from '@lib/components/Typography/Typography';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import styles from './SupportiveMessage.module.scss';

export enum SupportiveMessageColor {
  Blue = 'blue',
  Yellow = 'yellow',
  Gray = 'gray',
  Red = 'red',
}

export enum SupportiveMessageType {
  FilledWithBorder = 'filledWithBorder',
  FilledWithLeftBorder = 'filledWithLeftBorder',
  FilledOnly = 'filledOnly',
  Transparent = 'transparent',
}

export enum SupportiveMessageIconColor {
  Gray = 'iconGray',
  Inner = 'iconInner',
}

export enum SupportiveMessageIconType {
  FilledWithBorder = 'filledIconWithBorder',
  Filled = 'filledIcon',
}

export enum SupportiveMessageActionsPosition {
  Center = 'center',
  FlexStart = 'flex-start',
}

export interface SupportiveMessageProps {
  actions?: React.ReactNode;
  actionsPosition?: SupportiveMessageActionsPosition;
  className?: string;
  color?: SupportiveMessageColor;
  fullWidth?: boolean;
  hasBottomSpace?: boolean;
  iconColor?: SupportiveMessageIconColor;
  iconType?: SupportiveMessageIconType;
  leftIcon?: string;
  leftIconSymbolsOutlined?: boolean;
  text?: React.ReactNode;
  type?: SupportiveMessageType;
}

function SupportiveMessage(props: SupportiveMessageProps) {
  const {
    actions,
    actionsPosition = SupportiveMessageActionsPosition.Center,
    className,
    color = SupportiveMessageColor.Blue,
    fullWidth,
    hasBottomSpace = true,
    iconColor = SupportiveMessageIconColor.Gray,
    iconType = SupportiveMessageIconType.Filled,
    leftIcon,
    leftIconSymbolsOutlined,
    text,
    type = SupportiveMessageType.FilledWithLeftBorder,
  } = props;
  const hasActions = actions !== undefined;
  const hasLeftIcon = leftIcon !== undefined;
  if (!text) return null;
  const rootClassName = classNames(
    styles.messageRoot,
    {
      [styles.messageRootFullWidth]: fullWidth,
      [styles.messageRootFullBottomPadding]: hasBottomSpace,
    },
    className,
  );
  const messageClassName = classNames(
    styles.message,
    styles[color],
    styles[type],
    {
      [styles.withActions]: hasActions,
      [styles.withActionsFlexStart]:
        hasActions &&
        actionsPosition === SupportiveMessageActionsPosition.FlexStart,
      [styles.withLeftIcon]:
        hasLeftIcon && iconType !== SupportiveMessageIconType.FilledWithBorder,
      [styles.withBigLeftIcon]:
        hasLeftIcon && iconType === SupportiveMessageIconType.FilledWithBorder,
    },
  );
  const iconClassName = classNames(
    styles.leftIcon,
    styles[iconColor],
    styles[iconType],
  );
  return (
    <div className={rootClassName}>
      <div className={messageClassName}>
        {hasLeftIcon && (
          <span className={iconClassName}>
            <MaterialIcon
              icon={leftIcon}
              symbolsOutlined={leftIconSymbolsOutlined}
              size={
                iconType === SupportiveMessageIconType.FilledWithBorder ||
                type === SupportiveMessageType.Transparent
                  ? 'md-16'
                  : undefined
              }
            />
          </span>
        )}
        <Typography variant="label" block className={styles.messageContent}>
          {text}
        </Typography>
        {hasActions && <span className={styles.actions}>{actions}</span>}
      </div>
    </div>
  );
}

export default memo(SupportiveMessage);
